// src/components/modals/RefundInvoiceModal.jsx
import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

const RefundInvoiceModal = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
  invoice
}) => {
  const { t, i18n } = useTranslation()
  const isRTL = i18n.dir() === 'rtl'
  const [reason, setReason] = useState('')

  useEffect(() => {
    if (isOpen) {
      setReason('')
    }
  }, [isOpen])

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isLoading) {
      onClose()
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Escape' && !isLoading) {
      onClose()
    }
  }

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, isLoading])

  if (!isOpen || !invoice) {
    return null
  }

  const invoiceLine = `${invoice.invoice_number || ''} — ${invoice.client?.name || t('invoices.noClient', '-')} — ${Number(invoice.total || 0).toFixed(2)} ${invoice.currency || ''}`.trim()

  const modalContent = (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px'
      }}
      onClick={handleBackdropClick}
    >
      <div
        style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '16px',
          padding: '32px',
          maxWidth: '440px',
          width: '100%',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          fontFamily: '"Red Hat Display", sans-serif',
          direction: isRTL ? 'rtl' : 'ltr'
        }}
        onClick={(e) => e.stopPropagation()}
        dir={i18n.dir()}
        lang={i18n.language}
      >
        {/* Header */}
        <div style={{ marginBottom: '20px', textAlign: isRTL ? 'right' : 'center' }}>
          <div
            style={{
              width: '48px',
              height: '48px',
              backgroundColor: '#FEF2F2',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px auto'
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#EF4444"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <circle cx="12" cy="17" r="0.8" fill="currentColor" stroke="none" />
            </svg>
          </div>

          <h3
            style={{
              fontSize: '20px',
              fontWeight: 600,
              color: '#1F2937',
              margin: '0 0 8px 0'
            }}
          >
            {t('invoices.refundTitle', 'Refund Invoice')}
          </h3>

          <p
            style={{
              fontSize: '15px',
              color: '#374151',
              fontWeight: 500,
              margin: '0 0 8px 0',
              lineHeight: '1.5',
              wordBreak: 'break-word'
            }}
          >
            {invoiceLine}
          </p>

          <p
            style={{
              fontSize: '14px',
              color: '#6B7280',
              margin: 0,
              lineHeight: '1.5'
            }}
          >
            {t('invoices.refundWarning', 'This issues a full-reversal credit note for tax reporting. This cannot be undone.')}
          </p>
        </div>

        {/* Reason */}
        <div style={{ marginBottom: '24px' }}>
          <label
            htmlFor="refund-reason"
            style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: 500,
              color: '#374151',
              marginBottom: '8px',
              textAlign: isRTL ? 'right' : 'left'
            }}
          >
            {t('invoices.reasonLabel', 'Reason (optional)')}
          </label>
          <textarea
            id="refund-reason"
            rows={3}
            maxLength={500}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={isLoading}
            placeholder={t('invoices.reasonPlaceholder', 'e.g. Duplicate charge, service cancelled...')}
            style={{
              width: '100%',
              boxSizing: 'border-box',
              padding: '10px 14px',
              border: '1px solid #D1D5DB',
              borderRadius: '8px',
              fontSize: '14px',
              color: '#111827',
              background: '#FFFFFF',
              fontFamily: '"Red Hat Display", sans-serif',
              resize: 'vertical',
              lineHeight: '1.5'
            }}
          />
        </div>

        {/* Action Buttons */}
        <div
          style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}
        >
          <button
            onClick={onClose}
            disabled={isLoading}
            style={{
              padding: '12px 24px',
              backgroundColor: 'transparent',
              color: isLoading ? '#9CA3AF' : '#374151',
              border: '1px solid #D1D5DB',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: 500,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              fontFamily: '"Red Hat Display", sans-serif',
              minWidth: '80px'
            }}
            onMouseEnter={(e) => {
              if (!isLoading) {
                e.target.style.backgroundColor = '#F9FAFB'
                e.target.style.borderColor = '#9CA3AF'
              }
            }}
            onMouseLeave={(e) => {
              if (!isLoading) {
                e.target.style.backgroundColor = 'transparent'
                e.target.style.borderColor = '#D1D5DB'
              }
            }}
          >
            {t('invoices.cancel', 'Cancel')}
          </button>

          <button
            onClick={() => onConfirm(reason.trim() || undefined)}
            disabled={isLoading}
            style={{
              padding: '12px 24px',
              backgroundColor: isLoading ? '#FCA5A5' : '#EF4444',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: 500,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              fontFamily: '"Red Hat Display", sans-serif',
              minWidth: '80px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
            onMouseEnter={(e) => {
              if (!isLoading) {
                e.target.style.backgroundColor = '#DC2626'
              }
            }}
            onMouseLeave={(e) => {
              if (!isLoading) {
                e.target.style.backgroundColor = '#EF4444'
              }
            }}
          >
            {isLoading && (
              <div
                style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid #FFFFFF',
                  borderTop: '2px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}
              />
            )}
            {t('invoices.confirmRefund', 'Issue Refund')}
          </button>
        </div>
      </div>

      {/* Add keyframes for spinner animation */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  )

  // Render in portal to body
  return createPortal(modalContent, document.body)
}

export default RefundInvoiceModal
